import React from "react";
import { FiAlertTriangle, FiX, FiLoader } from "react-icons/fi";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "danger" | "warning" | "info";
  entityName?: string;
  entityType?: "user" | "agent" | "restaurant";
  isLoading?: boolean;
  loadingText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "danger",
  entityName,
  entityType,
  isLoading = false,
  loadingText = "Processing...",
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: "text-red-500",
          button: "bg-red-500 hover:bg-red-600 text-white",
          border: "border-red-200",
        };
      case "warning":
        return {
          icon: "text-yellow-500",
          button: "bg-yellow-500 hover:bg-yellow-600 text-white",
          border: "border-yellow-200",
        };
      case "info":
        return {
          icon: "text-blue-500",
          button: "bg-blue-500 hover:bg-blue-600 text-white",
          border: "border-blue-200",
        };
      default:
        return {
          icon: "text-red-500",
          button: "bg-red-500 hover:bg-red-600 text-white",
          border: "border-red-200",
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
        {/* Header */}
        <div className={`p-6 border-b ${styles.border}`}>
          <div className="flex items-center gap-3">
            <FiAlertTriangle className={`w-6 h-6 ${styles.icon}`} />
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 text-center leading-relaxed">
              {message}
            </p>
            {entityName && entityType && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium capitalize">{entityType}:</span>{" "}
                  {entityName}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                isLoading ? "opacity-75 cursor-not-allowed" : styles.button
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <FiLoader
                    className="w-4 h-4 animate-spin"
                    data-testid="loading-spinner"
                  />
                  <span>{loadingText}</span>
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
